import React, { Component } from 'react';
import { Checkbox, FormControlLabel, FormGroup, Typography, Divider, Grid, Collapse, IconButton, Box, Button, Skeleton } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { TabsFXReports, TabsFXReportsNames } from '../../Pages/FXReports/Interface';
import { Texts } from '../Reusables/Utility';

// Interface for component props
interface PermissionsProps {
  permissions: any;
  onPermissionsChange: (updatedPermissions: any) => void;
  isPermissionLoading?: boolean
  isReadOnly?: boolean

}

// Interface for component state
interface PermissionsState {
  expandedCategories: Record<string, boolean>;
  expandAll: boolean;

}

// Main component class
class CommonPermissonComp extends Component<PermissionsProps, PermissionsState> {
  constructor(props: PermissionsProps) {
    super(props);
    this.state = {
      expandedCategories: {},
      expandAll: false,

    };
    let tabsFXReports = Object.keys(TabsFXReports);
    for (let tab of tabsFXReports) {
      let label = (TabsFXReportsNames as any)[(TabsFXReports as any)[tab]];
      this.permissionLabels[`${Texts.isVisible}${tab}`] = `Show ${label}`;
      this.permissionLabels[`${Texts.isDownloadVisible}${tab}`] = `Download ${label}`;
    }
  }

  // Mapping of permission keys to their labels
  private permissionLabels: Record<string, string> = {
    isExchangeRateVisible: "Exchange Rate",
    isExchangeRateSaveBtnVisible: "Exchange Rate Save Action",
    isGeneralSettingsVisible: "General Settings",
    isGeneralSettingSaveBtnVisible: "General Setting Save Action",
    isPauseTransactionVisible: "Pause Transaction",
    isCardVerification: "Card Verification",
    isVisible: "Visible",
    isHeaderVisible: "Header Visible",
    isTransactionMetricsVisible: "Transaction Metrics",
    isFinancialMetricsVisible: "Financial Metrics",
    isRiskMetricsVisible: "Risk Metrics",
    isSupportMetricsVisible: "Support Metrics",
    isPerformanceMetricsVisible: "Performance Metrics",
    isDownloadVisible: "CSV Download",
    isFraudMarkVisible: "Fraud Mark",
    isRefundVisible: "Allow Refund",
    isZendeskNavigationAllowed: "Zendesk Navigation",
    isTransactionNavigationAllowed: "Transaction Navigation",
    isAdminTabVisible: "Admins List",
    isUsersTabVisible: "Mobile Users",
    isSupportRolesTabVisible: "Support Staffs",
    isEditUserInfoVisible: "Allow Edit User Info",
    isBlockUserVisible: "Block User",
    isUserSettingsVisible: "User Settings & Logs",
    isUserSettingSaveBtnVisible: "Allow Saving User Settings",
    isStatusHistoryVisible: "Status Change History",
    isUpdatePermissionVisible: "Enable Update Role",
    isAdminDownloadVisible: "Admin List CSV Download",
    isUserDownloadVisible: "User List CSV Download",
    isSupportRoleDownloadVisible: "Support Staffs CSV Download",
    isLimitHistoryVisible: "Limit History",
    isLimitHistoryDownloadVisible: "Limit History CSV Download",
    isAddPromoCodeVisible: "Create Coupon",
    isBlockCodeVisible: "Block",
    isActiveCodeVisible: "Activate",
    isAddCurrencyPairVisible: "Add Currency Pair",
    isEditRatesVisible: "Edit Rates",
    isActiveCurrencyAllowed: "Allow Active Currency",
    isInactiveCurrencyAllowed: "Allow Inactive Currency",
    isReferralSettingsVisible: "Referral Settings",
    isSaveSettingsAllowed: "Allow Save Settings",
    isViewRefereesVisible: "Referees List",
    isDownloadFileAllowed: "DownLoadFileAllowed",
    isSendEmailAllowd: "SendEmailAllowed",
    isAddLiquidityPartnerVisible: "Add Liquidity Partner",
    isEditPartnersVisible: "Edit Partner",
    isInActivePartnersVisible: "In-Active",
    isActivePartnersVisible: "Active"
  };


  // Handle individual checkbox change
  handleCheckboxChange = (category: string, key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { permissions, onPermissionsChange } = this.props;
    const updatedPermissions = { ...permissions };
    updatedPermissions[category][key] = event.target.checked;
    onPermissionsChange(updatedPermissions);
  }

  // Handle parent checkbox change (select/deselect all)
  handleParentCheckboxChange = (category: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { permissions, onPermissionsChange } = this.props;
    const updatedPermissions = { ...permissions };

    Object.keys(updatedPermissions[category]).forEach(key => {
      updatedPermissions[category][key] = event.target.checked;
    });

    onPermissionsChange(updatedPermissions);
  }

  // Toggle the expansion state of a category
  toggleCategoryExpansion = (category: string) => {
    this.setState((prevState) => ({
      expandedCategories: {
        ...prevState.expandedCategories,
        [category]: !prevState.expandedCategories[category],
      },
    }));
  };

  // Toggle the expansion state of all categories
  toggleExpandAll = () => {
    const newExpandState = !this.state.expandAll;
    this.setState({
      expandAll: newExpandState,
      expandedCategories: Object.keys(this.props.permissions).reduce((acc, category) => {
        acc[category] = newExpandState;
        return acc;
      }, {} as Record<string, boolean>),
    });
  };

  // Render checkboxes for a category
  renderCheckboxes = (category: string, items: any, parentChecked: boolean) => {

    return Object.keys(items)
      .filter((key) => key !== "isVisible")
      .map((key) => (
        <FormControlLabel
          key={key}
          control={
            <Checkbox
              checked={items[key]}
              onChange={this.handleCheckboxChange(category, key)}
              disabled={!parentChecked}
              color="secondary"
            />
          }
          label={this.formatLabel(key)}
          disabled={this.props.isReadOnly}
          style={{ marginLeft: '20px' }}
        />
      ));
  };

  // Format the label for a checkbox
  formatLabel = (key: string) => {
    return this.permissionLabels[key] || key;
  };

  // Render a category with its checkboxes
  renderCategory = (category: string, categoryLabel: string, items: any) => {
    const parentChecked = items ? Object.values(items).some(item => item) : false;
    const isExpanded = this.state.expandedCategories[category] || false;
    return (
      <div key={category}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            <FormControlLabel
              control={
                <Checkbox
                  checked={parentChecked}
                  onChange={this.handleParentCheckboxChange(category)}
                  color="secondary"
                />
              }
              label={categoryLabel}
              //required
              disabled={this.props.isReadOnly}
            />
          </Typography>
          <IconButton onClick={() => this.toggleCategoryExpansion(category)}>
            {isExpanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={isExpanded}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormGroup>
                {this.renderCheckboxes(category, items, parentChecked)}
              </FormGroup>
            </Grid>
          </Grid>
        </Collapse>
        <Divider />
      </div>
    );
  };

  render() {
    const { permissions, isPermissionLoading } = this.props;
    const { expandAll } = this.state;

    console.log("Permissions:", permissions);

    return (
      <div>
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2} marginTop="12px">
          <Typography variant="h6">Permissions</Typography>
          <Button variant="outlined" onClick={this.toggleExpandAll} size="small">
            {expandAll ? "Collapse All" : "Expand All"}
          </Button>
        </Box>

        {isPermissionLoading ? (
          <>
            {[...Array(8)].map((_, index) => (
              <Box key={index} display="flex" alignItems="center" paddingY={1}>
                <Skeleton animation="wave" variant="rectangular" width="100%" height={27} />
              </Box>
            ))}
          </>
        ) : (
          <>
            {this.renderCategory('profileSettings', 'Profile Menu', permissions.profileSettings)}
            {this.renderCategory('dashboard', 'Dashboard', permissions.dashboard)}
            {this.renderCategory('transactionLogs', 'Transaction Logs', permissions.transactionLogs)}
            {this.renderCategory('userLogs', 'User Logs', permissions.userLogs)}
            {this.renderCategory('complianceLogs', 'Compliance Logs', permissions.complianceLogs)}
            {this.renderCategory('userDetails', 'User Details', permissions.userDetails)}
            {this.renderCategory('userTransactionReport', 'User Transaction Report', permissions.userTransactionReport)}
            {this.renderCategory('promoCode', 'Promotionals', permissions.promoCode)}
            {this.renderCategory('exchangeRates', 'ExchangeRate', permissions.exchangeRates)}
            {this.renderCategory('referrals', 'Referrals', permissions.referrals)}
            {this.renderCategory('chargeBack', 'ChargeBack', permissions.chargeBack)}
            {this.renderCategory('partner', 'FX Partners', permissions.partner)}
            {this.renderCategory('fxReports', 'FX Reports', permissions.fxReports)}
          </>
        )}
      </div>
    );
  }
};

export default CommonPermissonComp;